import { Button, Logo } from '@convergence/components';
import { Browser, Dna, UsersThree } from '@phosphor-icons/react';
import { Application, Graphics, Color, Container, Ticker } from 'pixi.js';
import { useRef, useState, useEffect } from 'react';

class Dot {
  graphics: Graphics;
  constructor(x: number, y: number, radius: number, color: Color) {
    const graphics = new Graphics();
    graphics.circle(0, 0, radius).fill(new Color(color).setAlpha(0.3));
    graphics.position.set(x, y);
    this.graphics = graphics;
  }
}

class Line {
  rows: number;
  cols: number;
  dots: Dot[];
  delay: number = 0;
  timeEllapsed: number = 0;
  targetDots: [Dot, Dot] | null = null;
  opacity: number = 0;
  maxOpacity: number = 0.75;
  direction: number = 1;
  graphics: Graphics;
  constructor(dots: Dot[], rows: number, cols: number) {
    this.graphics = new Graphics();
    this.dots = dots;
    this.rows = rows;
    this.cols = cols;
    this.reset();
  }

  reset() {
    this.opacity = 0;
    this.timeEllapsed = 0;
    this.direction = 1;
    this.delay = Math.random() * 5000 + 500;
    this.graphics.clear();
    const randomDotIndex = Math.floor(Math.random() * this.dots.length);
    const dot = this.dots[randomDotIndex];
    if (!dot) {
      throw new Error('dot is undefined');
    }
    const dotRow = Math.floor(randomDotIndex / this.cols);
    let adjacentIndices: number[] = [];
    const maxDistance = 3;
    const distance = Math.ceil(Math.random() * maxDistance);
    const left = randomDotIndex - distance;
    const leftRow = Math.floor(left / this.cols);
    if (left >= 0 && leftRow === dotRow) {
      adjacentIndices.push(left);
    }
    const right = randomDotIndex + distance;
    const rightRow = Math.floor(right / this.cols);
    if (right < this.dots.length && rightRow === dotRow) {
      adjacentIndices.push(right);
    }
    const up = randomDotIndex - distance * this.cols;
    if (up >= 0) {
      adjacentIndices.push(up);
    }
    const down = randomDotIndex + distance * this.cols;
    if (down < this.dots.length) {
      adjacentIndices.push(down);
    }

    const randomAdjacentIndex = Math.floor(
      adjacentIndices[Math.floor(Math.random() * adjacentIndices.length)] ?? -1
    );
    if (randomAdjacentIndex === undefined || randomAdjacentIndex < 0) {
      throw new Error(
        `randomAdjacentIndex ${randomAdjacentIndex} is undefined`
      );
    }
    const adjacentDot = this.dots[randomAdjacentIndex];
    if (!adjacentDot) {
      throw new Error(`adjacentDot ${randomAdjacentIndex} is undefined`);
    }
    this.targetDots = [dot, adjacentDot];

    const posA = dot.graphics.position;
    const posB = adjacentDot.graphics.position;

    this.graphics.alpha = 0;
    this.graphics
      .fill(0xfbaa00, 1)
      .circle(posA.x, posA.y, 5)
      .circle(posB.x, posB.y, 5)
      .setStrokeStyle({ width: 1, color: 0xfbaa00, alpha: 1 })
      // .setStrokeStyle({ width: 1, color: 0x333333, alpha: 1 })
      .moveTo(posA.x, posA.y)
      .lineTo(posB.x, posB.y)
      .stroke();
  }

  update(t: Ticker) {
    this.timeEllapsed += t.deltaMS;
    if (this.timeEllapsed < this.delay) {
      return;
    }
    if (!this.targetDots) {
      this.reset();
    }
    this.opacity += 0.008 * this.direction;
    this.graphics.alpha = this.opacity;

    if (this.opacity >= this.maxOpacity) {
      this.direction = -1;
    }
    if (this.opacity < 0) {
      this.reset();
    }
  }
}

class Dots {
  private dots: Dot[] = [];
  private lines: Line[] = [];
  container = new Container();
  constructor(
    app: Application,
    width: number,
    height: number,
    size: number,
    gap = 72
  ) {
    const color = new Color(0x000000);
    const numCols = Math.floor((width + gap) / (size + gap));
    const numRows = Math.floor((height + gap) / (size + gap));
    const outerPaddingX = (width - (numCols * size + (numCols - 1) * gap)) / 2;
    const outerPaddingY = (height - (numRows * size + (numRows - 1) * gap)) / 2;

    let xPos = outerPaddingX;
    let yPos = outerPaddingY;
    for (let row = 0; row < numRows; row += 1) {
      for (let col = 0; col < numCols; col += 1) {
        const dot = new Dot(xPos, yPos, size, color);
        this.dots.push(dot);
        xPos += size + gap;
      }
      xPos = outerPaddingX;
      yPos += size + gap;
    }

    for (let i = 0; i < Math.floor(this.dots.length / 2); i += 1) {
      const line = new Line(this.dots, numRows, numCols);
      this.lines.push(line);
      this.container.addChild(line.graphics);

      // this.container.addChild(
      //   new Graphics()
      //     .moveTo(5, 50)
      //     .lineTo(780, 780)
      //     .stroke(0xff0000)
      //     .setStrokeStyle({
      //       width: 10,
      //       color: 0xff0000,
      //       alpha: 1
      //     })
      // );
    }

    for (const dot of this.dots) {
      this.container.addChild(dot.graphics);
    }

    app.ticker.add((t) => {
      this.lines.forEach((line) => {
        line.update(t);
      });
    });
  }
}

export function Landing() {
  const appInitialized = useRef(false);
  const [app, setApp] = useState<Application | null>(null);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  async function createApp() {
    if (ref) {
      const app = new Application();
      await app.init({
        backgroundAlpha: 0,
        resolution: window.devicePixelRatio || 1,
        resizeTo: ref,
        antialias: true
      });
      ref.appendChild(app.canvas);
      app.canvas.style.position = 'absolute';
      app.canvas.style.top = '0';
      app.canvas.style.left = '0';
      app.canvas.style.width = '100%';
      app.canvas.style.height = '100%';
      const width = Math.ceil(app.renderer.width);
      const height = Math.ceil(app.renderer.height);
      const size = 2;
      const gap = 54;
      const dots = new Dots(app, width, height, size, gap);
      app.stage.addChild(dots.container);
      setApp(app);
    }
  }

  useEffect(() => {
    createApp();
  }, [setApp, ref]);

  useEffect(() => {
    return () => {
      if (app && appInitialized.current) {
        app.destroy(true, { children: true });
        setApp(null);
      }
    };
  }, [app]);

  // useEffect(() => {
  //     // Initialize the application
  //     return () => {
  //         if (app) {
  //         }
  //     }
  // }, [app])

  const iconSize = 24;
  const features = [
    {
      title: 'Genomic Environments',
      description:
        'Everything you need to analyze 3D genomic data in one place.',
      icon: <Dna size={iconSize} />
    },
    {
      title: 'Collaborative Tools',
      description: 'Work together seamlessly with other researchers.',
      icon: <UsersThree size={iconSize} />
    },
    {
      title: 'Web-Based Interface',
      description:
        'Access your Tinker Box from anywhere, no installation required.',
      icon: <Browser size={iconSize} />
    }
  ];

  const tryFreeUrl = import.meta.env.VITE_ACCOUNT_URL + '/try-free';

  return (
    <div className="min-h-screen w-full flex flex-col relative">
      <div ref={setRef} className="w-full h-full absolute top-0 left-0"></div>
      {/* <div className="grid-background"></div> */}
      {/* Graph paper background */}
      <div className="relative z-10 ">
        {/* Navigation */}
        <nav className="border-b border-gray-200 py-4 px-6 md:px-12 bg-white">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="font-bold text-3xl tracking-tight font-terminal bg-white flex items-center gap-2">
              <Logo className="h-6" />
              Tinker
            </div>
            <div className="hidden md:flex space-x-8">
              <a
                href="#features"
                className="text-gray-800 hover:text-black transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-gray-800 hover:text-black transition-colors"
              >
                How It Works
              </a>
              {/* <a */}
              {/*   href="#contact" */}
              {/*   className="text-gray-800 hover:text-black transition-colors" */}
              {/* > */}
              {/*   Contact */}
              {/* </a> */}
            </div>
            <div className="flex space-x-4 items-center">
              <a
                href={import.meta.env.VITE_LOGIN_URL}
                className="px-4 py-2 border border-gray-800 text-gray-800 hover:bg-gray-100 transition-colors"
              >
                Login
              </a>
              <button className="md:hidden">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-20 px-72 ">
          <div className="grid md:grid-cols-1 items-center">
            <div className="bg-white">
              <h1 className="text-5xl font-bold mb-6 leading-tight font-terminal ">
                Supercharge Your Genomics Pipeline⚡
              </h1>
              <p className="text-xl text-gray-700 mb-8">
                A Tinker Box is a cloud computer for 3D genomics. It comes
                pre-loaded with AQuA-tools and sample datasets and runs directly
                in your browser with no downloads or account creation required.
              </p>
              <div className="group">
                <Button className="bg-orange-2 text-white hover:bg-orange-2">
                  <a
                    href={tryFreeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Try For Free
                  </a>
                </Button>
                <p className="text-xs p-1 group-hover:opacity-100 opacity-0">
                  * No personal info or credit card required
                </p>
              </div>
            </div>
            {/* <div className="relative h-64 md:h-auto"> */}
            {/*   <div className="absolute top-0 right-0 w-32 h-32 border-2 border-black rounded-md transform rotate-12"></div> */}
            {/*   <div className="absolute top-20 right-20 w-40 h-40 border-2 border-black rounded-full"></div> */}
            {/*   <div className="absolute bottom-0 left-10 w-24 h-24 border-2 border-black transform -rotate-6"></div> */}
            {/* </div> */}
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 px-6 md:px-12 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-16 text-center">Features</h2>
            <div className="flex justify-around gap-8">
              {features.map((feature) => (
                <div className="p-6 bg-white border border-black transition-shadow flex-[1] ">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    {feature.icon} {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          id="how-it-works"
          className="py-20 px-6 md:px-12 bg-white mb-48"
        >
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-16 text-center">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black text-white text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-2">Sign Up</h3>
                <p className="text-gray-600">
                  Create your account in seconds (No credit card needed)
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black text-white text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-2">Log On</h3>
                <p className="text-gray-600">
                  Logon to your new Tinker Box (No downloads or account creation
                  needed)
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black text-white text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-2">Tinker</h3>
                <p className="text-gray-600">
                  Start analyzing 3D genomic data with pre-loaded tools and
                  datasets
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {/* <section id="testimonials" className="py-20 px-6 md:px-12 bg-gray-50"> */}
        {/*   <div className="max-w-7xl mx-auto"> */}
        {/*     <h2 className="text-3xl font-bold mb-16 text-center"> */}
        {/*       What Our Users Say */}
        {/*     </h2> */}
        {/*     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"> */}
        {/*       <div className="p-6 bg-white border border-gray-200 rounded-md"> */}
        {/*         <p className="text-gray-600 mb-4"> */}
        {/*           "This platform has transformed how our team collaborates. The */}
        {/*           minimalist design keeps us focused on what matters." */}
        {/*         </p> */}
        {/*         <div className="font-bold">Alex Chen</div> */}
        {/*         <div className="text-gray-500 text-sm"> */}
        {/*           Product Manager, TechCorp */}
        {/*         </div> */}
        {/*       </div> */}
        {/*       <div className="p-6 bg-white border border-gray-200 rounded-md"> */}
        {/*         <p className="text-gray-600 mb-4"> */}
        {/*           "The graph-inspired interface is not just beautiful, it's */}
        {/*           incredibly functional. Our workflow efficiency has improved by */}
        {/*           40%." */}
        {/*         </p> */}
        {/*         <div className="font-bold">Sarah Johnson</div> */}
        {/*         <div className="text-gray-500 text-sm">COO, Startify</div> */}
        {/*       </div> */}
        {/*       <div className="p-6 bg-white border border-gray-200 rounded-md"> */}
        {/*         <p className="text-gray-600 mb-4"> */}
        {/*           "I've tried dozens of SaaS solutions, but this one stands out */}
        {/*           for its simplicity and power. It just works." */}
        {/*         </p> */}
        {/*         <div className="font-bold">Marcus Williams</div> */}
        {/*         <div className="text-gray-500 text-sm">Freelance Designer</div> */}
        {/*       </div> */}
        {/*     </div> */}
        {/*   </div> */}
        {/* </section> */}
        {/**/}
        {/* CTA Section */}
        <section className="py-20 px-6 md:px-12 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Start Tinkering?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              No information required. No credit card needed. Start tinkering in
              seconds.
            </p>
            <Button className="px-8 py-3 bg-black text-white hover:bg-gray-800 transition-colors">
              <a href={tryFreeUrl} target="_blank" rel="noopener noreferrer">
                Get Started Now
              </a>
            </Button>
          </div>
        </section>

        {/* Footer */}
        {/* <footer className="py-12 px-6 md:px-12 border-t border-gray-200"> */}
        {/*   <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8"> */}
        {/*     <div> */}
        {/*       <div className="font-mono font-bold text-xl mb-4">GRAPHSAAS</div> */}
        {/*       <p className="text-gray-600">Simplifying workflows since 2025.</p> */}
        {/*     </div> */}
        {/*     <div> */}
        {/*       <h3 className="font-bold mb-4">Product</h3> */}
        {/*       <ul className="space-y-2"> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Features */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Use Cases */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Security */}
        {/*           </a> */}
        {/*         </li> */}
        {/*       </ul> */}
        {/*     </div> */}
        {/*     <div> */}
        {/*       <h3 className="font-bold mb-4">Company</h3> */}
        {/*       <ul className="space-y-2"> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             About */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Blog */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Careers */}
        {/*           </a> */}
        {/*         </li> */}
        {/*       </ul> */}
        {/*     </div> */}
        {/*     <div> */}
        {/*       <h3 className="font-bold mb-4">Connect</h3> */}
        {/*       <ul className="space-y-2"> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             Twitter */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             LinkedIn */}
        {/*           </a> */}
        {/*         </li> */}
        {/*         <li> */}
        {/*           <a */}
        {/*             href="#" */}
        {/*             className="text-gray-600 hover:text-black transition-colors" */}
        {/*           > */}
        {/*             GitHub */}
        {/*           </a> */}
        {/*         </li> */}
        {/*       </ul> */}
        {/*     </div> */}
        {/*   </div> */}
        {/*   <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm"> */}
        {/*     © 2025 GraphSaaS. All rights reserved. */}
        {/*   </div> */}
        {/* </footer> */}
      </div>
    </div>
    // <main className="min-h-screen w-full flex flex-col">
    //   <div className="grid-background"></div>
    // </main>
  );
}
